import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type Aggregation,
  type AlertChannel,
  type AlertCondition,
  type AlertEvent,
  type AlertRule,
  type ChannelType,
  type DatasetSummary,
} from '../api';
import { Notice } from './Panels';

/**
 * Alerts: rules over a dataset, the channels they notify, and the history of
 * every evaluation.
 *
 * The history is the point. An alert that never fires looks identical to an
 * alert that never runs, so the events table shows every evaluation — ok,
 * firing, suppressed by cooldown, and failed — with each delivery's outcome.
 * A failed delivery is shown in red where it happened, never dropped.
 */

const AGGREGATIONS: { id: Aggregation; label: string }[] = [
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'count', label: 'Row count' },
  { id: 'countDistinct', label: 'Distinct count' },
  { id: 'min', label: 'Minimum' },
  { id: 'max', label: 'Maximum' },
  { id: 'median', label: 'Median' },
];

const CONDITIONS: { id: AlertCondition; label: string; hint: string }[] = [
  { id: 'above', label: 'Rises above', hint: 'the threshold' },
  { id: 'below', label: 'Falls below', hint: 'the threshold' },
  {
    id: 'changeByPercent',
    label: 'Changes by ±% vs previous evaluation',
    hint: 'the percentage',
  },
];

const CHANNEL_TYPES: { id: ChannelType; label: string; placeholder: string }[] = [
  { id: 'email', label: 'Email', placeholder: 'alerts@example.com' },
  { id: 'slack', label: 'Slack webhook', placeholder: 'https://hooks.slack.com/services/…' },
  { id: 'webhook', label: 'Generic webhook', placeholder: 'https://example.com/hooks/datavantage' },
];

export function Alerts() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [channels, setChannels] = useState<AlertChannel[]>([]);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<AlertRule | 'new' | null>(null);

  const reload = useCallback(() => {
    api.listAlertRules().then((r) => setRules(r.rules)).catch(() => setRules([]));
    api.listAlertChannels().then((r) => setChannels(r.channels)).catch(() => setChannels([]));
    api.listAlertEvents().then((r) => setEvents(r.events)).catch(() => setEvents([]));
    api.listDatasets().then((r) => setDatasets(r.datasets)).catch(() => setDatasets([]));
  }, []);

  useEffect(reload, [reload]);

  const datasetName = (id: string): string =>
    datasets.find((d) => d.id === id)?.name ?? 'a deleted dataset';

  return (
    <div className="page">
      <header className="intro">
        <h2>Alerts</h2>
        <p>
          A rule measures one metric on one dataset each time the scheduler runs, and notifies its
          channels when the condition holds. Cooldown keeps a metric that stays over the line from
          paging you every five minutes.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
      {notice !== null && <Notice tone="good" message={notice} />}

      <RulesCard
        rules={rules}
        channels={channels}
        datasetName={datasetName}
        onNew={() => setEditing('new')}
        onEdit={setEditing}
        onChanged={(message) => {
          if (message !== null) setNotice(message);
          reload();
        }}
        onFailure={setFailure}
      />

      {editing !== null && (
        <RuleForm
          rule={editing === 'new' ? null : editing}
          datasets={datasets}
          channels={channels}
          onDone={(message) => {
            setEditing(null);
            if (message !== null) setNotice(message);
            reload();
          }}
          onFailure={setFailure}
        />
      )}

      <EventsCard events={events} rules={rules} datasetName={datasetName} />

      <ChannelsCard
        channels={channels}
        onChanged={(message) => {
          if (message !== null) setNotice(message);
          reload();
        }}
        onFailure={setFailure}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Rules
// -----------------------------------------------------------------------------

function RulesCard({
  rules,
  channels,
  datasetName,
  onNew,
  onEdit,
  onChanged,
  onFailure,
}: {
  rules: AlertRule[];
  channels: AlertChannel[];
  datasetName: (id: string) => string;
  onNew: () => void;
  onEdit: (rule: AlertRule) => void;
  onChanged: (message: string | null) => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  return (
    <section className="card">
      <header>
        <h2>Rules</h2>
        <span className="sub">
          {rules.length} configured · evaluated every scheduler tick
        </span>
      </header>

      <button className="primary" onClick={onNew} style={{ alignSelf: 'flex-start' }}>
        New rule
      </button>

      {rules.length === 0 ? (
        <p className="muted" style={{ marginTop: 10 }}>
          No rules yet. A rule watches a metric — say, sum of revenue — and messages a channel when
          it crosses a line you set.
        </p>
      ) : (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Metric</th>
                <th>Condition</th>
                <th>State</th>
                <th className="num">Last value</th>
                <th>Last fired</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    {rule.name}
                    {!rule.active && <span className="badge" style={{ marginLeft: 6 }}>paused</span>}
                  </td>
                  <td>
                    {describeMetric(rule)} <span className="muted">on {datasetName(rule.datasetId)}</span>
                  </td>
                  <td>{describeCondition(rule)}</td>
                  <td>
                    {rule.lastState === null ? (
                      <span className="muted">not evaluated yet</span>
                    ) : rule.lastState === 'firing' ? (
                      <span className="badge critical">firing</span>
                    ) : (
                      <span className="badge good">ok</span>
                    )}
                  </td>
                  <td className="num">
                    {rule.lastValue === null
                      ? '—'
                      : rule.lastValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </td>
                  <td>
                    {rule.lastFiredAt === null ? (
                      <span className="muted">never</span>
                    ) : (
                      new Date(rule.lastFiredAt).toLocaleString()
                    )}
                  </td>
                  <td>
                    <span className="row" style={{ gap: 6 }}>
                      <button className="ghost tiny" onClick={() => onEdit(rule)}>
                        Edit
                      </button>
                      <button
                        className="ghost tiny"
                        onClick={async () => {
                          try {
                            await api.updateAlertRule(rule.id, { active: !rule.active });
                            onChanged(null);
                          } catch (error) {
                            if (error instanceof ApiFailure) onFailure(error);
                          }
                        }}
                      >
                        {rule.active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        className="ghost tiny danger"
                        onClick={async () => {
                          try {
                            await api.deleteAlertRule(rule.id);
                            onChanged(`Rule "${rule.name}" deleted.`);
                          } catch (error) {
                            if (error instanceof ApiFailure) onFailure(error);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rules.length > 0 && channels.length === 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice
            message="No notification channels are configured."
            remedy="Add one below — a rule with no channels records its firings here but cannot tell anyone."
          />
        </div>
      )}
    </section>
  );
}

function RuleForm({
  rule,
  datasets,
  channels,
  onDone,
  onFailure,
}: {
  rule: AlertRule | null;
  datasets: DatasetSummary[];
  channels: AlertChannel[];
  onDone: (message: string | null) => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [datasetId, setDatasetId] = useState(rule?.datasetId ?? datasets[0]?.id ?? '');
  const [aggregation, setAggregation] = useState<Aggregation>(rule?.metric.aggregation ?? 'sum');
  const [column, setColumn] = useState<string>(rule?.metric.column ?? '');
  const [columns, setColumns] = useState<string[]>([]);
  const [condition, setCondition] = useState<AlertCondition>(rule?.condition ?? 'above');
  const [threshold, setThreshold] = useState(String(rule?.threshold ?? ''));
  const [cooldownMinutes, setCooldownMinutes] = useState(String(rule?.cooldownMinutes ?? 60));
  const [channelIds, setChannelIds] = useState<string[]>(rule?.channelIds ?? []);
  const [busy, setBusy] = useState(false);

  // The column dropdown needs the dataset's schema, which the summary does
  // not carry.
  useEffect(() => {
    if (datasetId === '') {
      setColumns([]);
      return;
    }
    api
      .getDataset(datasetId)
      .then((r) =>
        setColumns(
          r.dataset.columns
            .filter((c) => c.type === 'numeric' && !c.isIdentifier)
            .map((c) => c.name),
        ),
      )
      .catch(() => setColumns([]));
  }, [datasetId]);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const body = {
        name,
        datasetId,
        metric: { column: aggregation === 'count' ? null : column, aggregation },
        condition,
        threshold: Number(threshold),
        cooldownMinutes: Number(cooldownMinutes),
        channelIds,
        active: true,
      };
      if (rule === null) {
        await api.createAlertRule(body);
        onDone(`Rule "${name}" created.`);
      } else {
        await api.updateAlertRule(rule.id, body);
        onDone(`Rule "${name}" updated.`);
      }
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setBusy(false);
    }
  }

  const needsColumn = aggregation !== 'count';
  const conditionHint = CONDITIONS.find((c) => c.id === condition)?.hint ?? 'the threshold';

  return (
    <section className="card">
      <header>
        <h2>{rule === null ? 'New rule' : `Edit “${rule.name}”`}</h2>
        <span className="sub">query + condition + channels</span>
      </header>

      <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
        <label className="field">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Revenue dropped"
            maxLength={120}
          />
        </label>

        <label className="field">
          Dataset
          <select value={datasetId} onChange={(e) => setDatasetId(e.target.value)}>
            {datasets.length === 0 && <option value="">No datasets yet — add a source first</option>}
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.rowCount.toLocaleString('en-US')} rows)
              </option>
            ))}
          </select>
        </label>

        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="field grow">
            Metric
            <select
              value={aggregation}
              onChange={(e) => setAggregation(e.target.value as Aggregation)}
            >
              {AGGREGATIONS.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>

          {needsColumn && (
            <label className="field grow">
              Column
              <select value={column} onChange={(e) => setColumn(e.target.value)}>
                <option value="">Pick a numeric column…</option>
                {columns.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="field grow">
            Condition
            <select value={condition} onChange={(e) => setCondition(e.target.value as AlertCondition)}>
              {CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            Threshold
            <input
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="1000"
            />
            <span className="field-help">
              Fires when the metric {condition === 'above' ? 'exceeds' : condition === 'below' ? 'drops under' : 'moves by at least'} this number.
            </span>
          </label>

          <label className="field">
            Cooldown (minutes)
            <input
              type="number"
              min={1}
              max={10080}
              value={cooldownMinutes}
              onChange={(e) => setCooldownMinutes(e.target.value)}
            />
            <span className="field-help">
              After firing, the rule stays quiet for this long even if {conditionHint} is still crossed.
            </span>
          </label>
        </div>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend className="field" style={{ padding: 0 }}>
            Notify channels
          </legend>
          {channels.length === 0 ? (
            <p className="muted">No channels yet — add one in the Channels section below.</p>
          ) : (
            channels.map((channel) => (
              <label className="check" key={channel.id}>
                <input
                  type="checkbox"
                  checked={channelIds.includes(channel.id)}
                  onChange={(e) =>
                    setChannelIds(
                      e.target.checked
                        ? [...channelIds, channel.id]
                        : channelIds.filter((id) => id !== channel.id),
                    )
                  }
                />
                {channel.name} <span className="muted">({channel.type} · {channel.target})</span>
              </label>
            ))
          )}
        </fieldset>

        <div className="row" style={{ gap: 8 }}>
          <button
            className="primary"
            disabled={
              busy || name.trim() === '' || datasetId === '' || threshold.trim() === '' ||
              (needsColumn && column === '')
            }
            onClick={() => void save()}
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? <span className="spinner" /> : rule === null ? 'Create rule' : 'Save rule'}
          </button>
          <button className="ghost" onClick={() => onDone(null)}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Event history
// -----------------------------------------------------------------------------

function EventsCard({
  events,
  rules,
  datasetName,
}: {
  events: AlertEvent[];
  rules: AlertRule[];
  datasetName: (id: string) => string;
}) {
  const ruleName = (id: string): string => rules.find((r) => r.id === id)?.name ?? 'a deleted rule';

  return (
    <section className="card">
      <header>
        <h2>History</h2>
        <span className="sub">every evaluation, not just the loud ones</span>
      </header>

      {events.length === 0 ? (
        <p className="muted" style={{ marginTop: 10 }}>
          Nothing evaluated yet. Events appear after the scheduler's next tick.
        </p>
      ) : (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Rule</th>
                <th>State</th>
                <th className="num">Value</th>
                <th>Deliveries</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{new Date(event.evaluatedAt).toLocaleString()}</td>
                  <td>
                    {ruleName(event.ruleId)}{' '}
                    <span className="muted">on {datasetName(event.datasetId)}</span>
                  </td>
                  <td>
                    {event.error !== null ? (
                      <span className="badge critical" title={event.error}>
                        evaluation failed
                      </span>
                    ) : event.state === 'firing' ? (
                      <span className="badge critical">
                        firing{event.suppressed ? ' · cooldown' : ''}
                      </span>
                    ) : (
                      <span className="badge good">ok</span>
                    )}
                  </td>
                  <td className="num">
                    {event.value === null
                      ? '—'
                      : event.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </td>
                  <td>
                    {event.deliveries.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {event.deliveries.map((delivery, i) => (
                          <span
                            key={i}
                            className={`badge ${delivery.delivered ? 'good' : 'critical'}`}
                            title={delivery.error ?? ''}
                          >
                            {delivery.channelName} {delivery.delivered ? '✓' : '✗'}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {events.some((e) => e.deliveries.some((d) => !d.delivered)) && (
        <div style={{ marginTop: 12 }}>
          <Notice
            tone="error"
            message="Some notifications could not be delivered."
            remedy="Hover a red channel badge for the reason, then use Test on that channel below."
          />
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Channels
// -----------------------------------------------------------------------------

function ChannelsCard({
  channels,
  onChanged,
  onFailure,
}: {
  channels: AlertChannel[];
  onChanged: (message: string | null) => void;
  onFailure: (failure: ApiFailure) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<ChannelType>('slack');
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ channelId: string; delivered: boolean; detail: string } | null>(null);

  const typeMeta = CHANNEL_TYPES.find((t) => t.id === type)!;

  async function add(): Promise<void> {
    setBusy(true);
    try {
      await api.createAlertChannel({
        type,
        name,
        target,
        ...(type === 'webhook' && secret.trim() !== '' ? { secret } : {}),
      });
      setAdding(false);
      setName('');
      setTarget('');
      setSecret('');
      onChanged(`Channel "${name}" added.`);
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setBusy(false);
    }
  }

  async function test(channel: AlertChannel): Promise<void> {
    setTesting(channel.id);
    setTestResult(null);
    try {
      const result = await api.testAlertChannel(channel.id);
      setTestResult({ channelId: channel.id, delivered: result.delivered, detail: result.detail });
      onChanged(null);
    } catch (error) {
      if (error instanceof ApiFailure) onFailure(error);
    } finally {
      setTesting(null);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>Notification channels</h2>
        <span className="sub">defined once, reused by rules and scheduled digests</span>
      </header>

      {!adding && (
        <button className="primary" onClick={() => setAdding(true)} style={{ alignSelf: 'flex-start' }}>
          Add a channel
        </button>
      )}

      {adding && (
        <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="field">
              Type
              <select value={type} onChange={(e) => setType(e.target.value as ChannelType)}>
                {CHANNEL_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field grow">
              Name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="#finance-alerts"
                maxLength={120}
              />
            </label>
          </div>

          <label className="field">
            {type === 'email' ? 'Address' : 'Webhook URL'}
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={typeMeta.placeholder}
            />
            {type !== 'email' && (
              <span className="field-help">
                The URL is a secret — anyone holding it can post to the channel. It is stored for
                delivery but never shown in full again.
              </span>
            )}
          </label>

          {type === 'webhook' && (
            <label className="field">
              Signing secret (optional)
              <input
                type="text"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Sent as an HMAC signature in x-datavantage-signature"
              />
            </label>
          )}

          <div className="row" style={{ gap: 8 }}>
            <button
              className="primary"
              disabled={busy || name.trim() === '' || target.trim() === ''}
              onClick={() => void add()}
              style={{ alignSelf: 'flex-start' }}
            >
              {busy ? <span className="spinner" /> : 'Add channel'}
            </button>
            <button className="ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {channels.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Target</th>
                <th>Last delivery</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {channels.map((channel) => (
                <tr key={channel.id}>
                  <td>
                    {channel.name}
                    {!channel.active && <span className="badge" style={{ marginLeft: 6 }}>paused</span>}
                  </td>
                  <td>{channel.type}</td>
                  <td>
                    <code>{channel.target}</code>
                  </td>
                  <td>
                    {channel.lastDeliveryStatus === null ? (
                      <span className="muted">never used</span>
                    ) : channel.lastDeliveryStatus === 'ok' ? (
                      <span className="badge good">ok</span>
                    ) : (
                      <span className="badge critical" title={channel.lastDeliveryError ?? ''}>
                        failed
                      </span>
                    )}
                    {testResult !== null && testResult.channelId === channel.id && (
                      <span className="muted" style={{ marginLeft: 6 }}>
                        {testResult.detail}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="row" style={{ gap: 6 }}>
                      <button
                        className="ghost tiny"
                        disabled={testing === channel.id}
                        onClick={() => void test(channel)}
                      >
                        {testing === channel.id ? <span className="spinner" /> : 'Test'}
                      </button>
                      <button
                        className="ghost tiny"
                        onClick={async () => {
                          try {
                            await api.updateAlertChannel(channel.id, { active: !channel.active });
                            onChanged(null);
                          } catch (error) {
                            if (error instanceof ApiFailure) onFailure(error);
                          }
                        }}
                      >
                        {channel.active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        className="ghost tiny danger"
                        onClick={async () => {
                          try {
                            await api.deleteAlertChannel(channel.id);
                            onChanged(`Channel "${channel.name}" deleted.`);
                          } catch (error) {
                            if (error instanceof ApiFailure) onFailure(error);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function describeMetric(rule: AlertRule): string {
  const aggregation = AGGREGATIONS.find((a) => a.id === rule.metric.aggregation)?.label ?? rule.metric.aggregation;
  return rule.metric.column === null ? aggregation : `${aggregation} of ${rule.metric.column}`;
}

function describeCondition(rule: AlertRule): string {
  switch (rule.condition) {
    case 'above':
      return `> ${rule.threshold.toLocaleString('en-US')}`;
    case 'below':
      return `< ${rule.threshold.toLocaleString('en-US')}`;
    case 'changeByPercent':
      return `±${rule.threshold.toLocaleString('en-US')}%`;
  }
}
